import { sprite } from '../kit';
import { C, type Pix } from '../pix';
import { registerIcons } from '../store';
import { daoBlade, formedBlade, gem, R, ramp, wrappedGrip } from './craft';

/**
 * Hand-made item pictures: sabres (刀), hook swords, the wavy dagger, the claw, and the legend
 * blades. Grip bottom left, point top right, as the older sword icons lie.
 */

const blood = ramp('#c8283a');
const shadowSteel = ramp('#4a4a6a');

/** A ring pommel (the dao's iron ring) at (x,y). */
function ringPommel(p: Pix, x: number, y: number, col = R.iron): void {
  p.ring(x, y, 2.2, 2.2, col.d1);
  p.px(x + 1, y - 2, col.l1).px(x + 2, y - 1, col.l1);
}

/** A hook sword lying from grip (x0,y0) to the hook top (x1,y1), with a crescent hand guard. */
function hookSword(p: Pix, x0: number, y0: number, x1: number, y1: number, flip: boolean): void {
  const s = flip ? -1 : 1;
  formedBlade(p, x0 + 4 * s, y0 - 4, x1, y1, R.steel, { w: 2.6, taper: 0.95, ridge: false });
  // The hook: a tight curl back down at the top.
  p.thick(x1, y1, x1 + 3 * s, y1 - 1, R.steel.b, 2);
  p.thick(x1 + 3 * s, y1 - 1, x1 + 4 * s, y1 + 2, R.steel.d1, 2);
  p.px(x1 + 4 * s, y1 + 3, R.steel.l2);
  // Crescent guard across the grip.
  p.ring(x0 + 5 * s, y0 - 3, 4, 3, R.steel.l1, flip ? 0.2 : Math.PI - 0.2 - 2.4, flip ? 2.6 : Math.PI - 0.2);
  wrappedGrip(p, x0, y0, x0 + 4 * s, y0 - 4, R.ink);
  p.px(x0 - s, y0 + 1, R.steel.b);
}

registerIcons('item', {
  // ดาบเหล็กสันหนา: a heavy plain dao, thick back, iron ring pommel.
  'iron-dao': (p) => {
    daoBlade(p, 10, 21, 28, 3, 2.6, R.iron, { belly: 0.6 });
    p.thick(8, 20, 11, 23, R.bronze.d1, 2);
    p.px(10, 20, R.bronze.l1);
    wrappedGrip(p, 5, 27, 8, 24, R.wood);
    ringPommel(p, 4, 28);
  },
  // ดาบใหญ่พันผ้าชาด: a broad heavy dao, its grip wrapped in cinnabar cloth that trails.
  'great-dao': (p) => {
    daoBlade(p, 11, 20, 28, 3, 3.4, R.steel, { belly: 0.8 });
    p.rect(8, 19, 4, 4, R.gold.d1);
    p.px(11, 19, R.gold.l2);
    wrappedGrip(p, 4, 27, 8, 23, R.red);
    // The trailing cinnabar cloth, two folds.
    p.path(
      [
        [5, 26],
        [3, 21],
        [2, 16],
      ],
      R.red.b,
      2,
    );
    p.path(
      [
        [6, 27],
        [8, 30],
      ],
      R.red.d1,
      2,
    );
    p.px(3, 15, R.red.l1).px(4, 20, R.red.l1).px(9, 30, R.red.d2);
  },
  // ขอเกี่ยวคู่เสี้ยวเงิน: a crossed pair of silver hook swords.
  'twin-hooks': (p) => {
    hookSword(p, 4, 29, 24, 6, false);
    hookSword(p, 28, 29, 8, 6, true);
  },
  // มีดสั้นเขี้ยวงู: a wavy dagger with a viper-head pommel.
  'viper-kris': (p) => {
    const pts: [number, number][] = [];
    for (let i = 0; i <= 14; i++) {
      const t = i / 14;
      pts.push([13 + t * 14 + Math.sin(t * Math.PI * 4) * 1.4, 18 - t * 14 + Math.sin(t * Math.PI * 4) * 1.4]);
    }
    for (let i = 0; i < pts.length - 1; i++) {
      const [ax, ay] = pts[i]!;
      const [bx, by] = pts[i + 1]!;
      const w = i < 11 ? 3 : 2;
      p.thick(ax, ay, bx, by, R.steel.d1, w);
    }
    p.path(pts, R.steel.b);
    p.path(
      pts.slice(0, 11).map(([x, y]) => [x - 1, y - 1] as [number, number]),
      R.steel.l2,
    );
    // Guard: a jade-green asymmetric hilt flare.
    p.poly(
      [
        [10, 18],
        [14, 15],
        [17, 20],
        [13, 22],
      ],
      R.jade.d1,
    );
    p.line(11, 18, 14, 16, R.jade.l1);
    // Grip and a snake head biting the end.
    p.thick(6, 26, 11, 20, R.wood.d1, 3);
    for (const [x, y] of [
      [7, 24],
      [9, 22],
    ] as const)
      p.px(x, y, R.wood.l1);
    sprite(p, 2, 25, ['.gg..', 'ggGg.', 'gyggg', '.gggg', '..gg.', '...r.'], {
      g: R.jade.b,
      G: R.jade.l2,
      y: C.gold,
      r: C.blood,
    });
  },
  // กรงเล็บเหล็กดำ: a black-iron claw gauntlet, four hooked talons.
  'iron-claw': (p) => {
    // Cuff and back of the hand.
    p.poly(
      [
        [5, 28],
        [6, 18],
        [15, 13],
        [20, 16],
        [14, 28],
      ],
      R.ink.b,
    );
    p.poly(
      [
        [5, 28],
        [6, 22],
        [12, 22],
        [14, 28],
      ],
      R.wood.d1,
    );
    p.line(6, 22, 12, 22, R.bronze.b);
    p.line(6, 25, 13, 25, R.wood.b);
    for (const [x, y] of [
      [9, 19],
      [13, 17],
    ] as const)
      p.px(x, y, R.bronze.l1);
    // Talons: each a tapering curve, lit on top.
    const talons: [number, number, number, number][] = [
      [8, 18, 13, 5],
      [12, 16, 19, 5],
      [15, 15, 24, 8],
      [18, 16, 28, 13],
    ];
    for (const [ax, ay, bx, by] of talons) {
      p.thick(ax, ay, (ax + bx) / 2 + 1, (ay + by) / 2, R.iron.d1, 2);
      p.line((ax + bx) / 2 + 1, (ay + by) / 2, bx, by, R.iron.b);
      p.px(bx, by, R.iron.l2);
      p.px(bx - 1, by + 1, R.iron.l1);
    }
  },
  // กระบี่ซ่อนเงา (legend): a dusk-steel jian with a violet shadow twin trailing behind it.
  'u-shadow-sword': (p) => {
    // The shadow twin, offset down-left, flat violet clusters.
    formedBlade(p, 7, 27, 24, 10, R.violet, { w: 2.6, taper: 0.88, ridge: false });
    p.px(21, 14, R.violet.l2).px(17, 18, R.violet.l1);
    // The blade itself.
    formedBlade(p, 10, 22, 29, 3, shadowSteel, { w: 3, taper: 0.86 });
    p.thick(8, 21, 12, 25, R.violet.d1, 2);
    p.px(8, 21, R.violet.l1).px(12, 25, R.violet.l1);
    wrappedGrip(p, 5, 27, 9, 23, R.ink);
    gem(p, 4, 28, 1.6, R.violet);
    for (const [x, y] of [
      [27, 9],
      [22, 4],
      [29, 12],
    ] as const)
      p.px(x, y, R.violet.l2);
  },
  // ดาบโลหิตกลืนจันทร์ (legend): a blood-dark dao before a crescent moon.
  'u-moon-sabre': (p) => {
    // A crescent moon low behind the blade, eaten from the right.
    for (let y = 2; y < 22; y++)
      for (let x = 1; x < 20; x++) {
        const a = Math.hypot(x + 0.5 - 9, y + 0.5 - 10);
        const b = Math.hypot(x + 0.5 - 12.5, y + 0.5 - 8);
        if (a < 7.5 && b >= 6.2) p.px(x, y, a > 6.5 ? '#c8b48a' : x < 5 ? '#e8dcb8' : '#f6eed4');
      }
    daoBlade(p, 10, 21, 28, 3, 3, blood, { belly: 0.7 });
    p.ell(9, 21, 2.2, 2.2, R.gold.d1);
    p.px(10, 20, R.gold.l2);
    wrappedGrip(p, 5, 27, 8, 24, R.ink);
    ringPommel(p, 4, 28, R.gold);
    // Blood beading off the edge.
    p.px(22, 15, blood.b).px(22, 17, blood.d1).px(25, 12, blood.l1);
  },
});
